import { environment_dev } from '../../../environments/environment.dev';
import { environment_test } from '../../../environments/environment.test';
import { environment_us_dev } from '../../../environments/us/environment.us.dev';
import { environment_us_test } from '../../../environments/us/environment.us.test';
import { getPxdDevEnvironmentFromSubdomain } from './environment.pxd-dev';

/**
 * Dev-only environment override — SHIPS DISABLED.
 *
 * The Microsoft/Google IDP sign-in buttons on `LoginPage` never take a domain input, so
 * they resolve their endpoints from the "current" environment — which `EnvironmentService`
 * resets to prod on every load. That makes them awkward to test against a non-prod backend.
 * When this override is enabled, every prod fallback in `EnvironmentService` resolves to
 * {@link TEST_ENV_OVERRIDE_TARGET} instead, pointing the WHOLE app (sign-in, token
 * exchange, sign-out) at a chosen dev/test environment.
 *
 * Usage:
 *  - Enable: set {@link TEST_ENV_OVERRIDE_ENABLED} to `true`.
 *  - Switch environment: change {@link TEST_ENV_OVERRIDE_TARGET}.
 *  - Disable (normal prod-default behaviour): set {@link TEST_ENV_OVERRIDE_ENABLED} to `false`.
 *
 * ⚠️ Reminder — an enabled override diverts the WHOLE app off prod, so it's expected on
 * local and internal-test branches (those builds are meant to hit a dev environment). Just
 * be deliberate: don't carry an enabled override into a branch with an open PR (i.e. anything
 * heading for `main`) unless you genuinely intend to ship it. A console warning is logged at
 * startup whenever it is active (see getTestEnvOverride).
 */

// Master switch. Fine to enable on local / internal-test branches; just don't let an
// enabled override ride into a PR to `main` unless you really mean to (see reminder above).
// Typed as boolean (not the literal `false`) so toggling to `true` doesn't trip
// "unreachable code" / "always falsy" lint in EnvironmentService.
export const TEST_ENV_OVERRIDE_ENABLED: boolean = false;

// The environment the whole app falls back to while the override is enabled.
// Use one of the named keys handled below, or any "<sub>.printix.dev" subdomain
// (e.g. 'dev03.printix.dev'). Typed as string so the switch below type-checks.
export const TEST_ENV_OVERRIDE_TARGET: string = 'devenv.us.printix.net';

export interface ITestEnvOverride {
  endpoints: any;
  prefix: string;
}

/**
 * Resolves the configured override to a concrete environment + storage prefix, or
 * `null` when the override is disabled (so callers fall back to their normal behaviour).
 */
export function getTestEnvOverride(): ITestEnvOverride | null {
  if (!TEST_ENV_OVERRIDE_ENABLED) { return null; }
  // Startup reminder that the app is NOT pointed at prod. Expected on local / internal-test
  // builds; if you see it on a build heading for main, the override likely rode into a PR
  // by accident — double-check that's intended.
  console.warn(
    `[EnvironmentService] DEV ENV OVERRIDE ACTIVE — whole app pointed at ` +
    `"${TEST_ENV_OVERRIDE_TARGET}", not prod. Expected on internal-test builds; ` +
    `don't carry it into a PR to main unless intended.`
  );
  switch (TEST_ENV_OVERRIDE_TARGET) {
    case 'devenv.printix.net':
      return { endpoints: environment_dev, prefix: 'environment_dev' };
    case 'testenv.printix.net':
      return { endpoints: environment_test, prefix: 'environment_test' };
    case 'devenv.us.printix.net':
      return { endpoints: environment_us_dev, prefix: 'environment_us_dev' };
    case 'testenv.us.printix.net':
      return { endpoints: environment_us_test, prefix: 'environment_us_test' };
    default: {
      // Anything else is treated as a printix.dev subdomain host, e.g. 'dev03.printix.dev'.
      const endpoints = getPxdDevEnvironmentFromSubdomain(TEST_ENV_OVERRIDE_TARGET);
      return { endpoints, prefix: endpoints.environmentPrefix };
    }
  }
}
